import * as React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import SpeedIcon from '@mui/icons-material/Speed';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { LineChart } from '@mui/x-charts';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import AppNavbar from './AppNavbar';
import Header from './Header';
import SideMenu from './SideMenu';
import AppTheme from '../../shared-theme/AppTheme';
import GrowSelector from './GrowSelector';
import GrowProgressBar from './GrowProgressBar';
import GrowReminders from './GrowReminders';
import SensorOverview from './SensorOverview';
import SensorStatusBar from './SensorStatusBar';
import ConfigurableSensorSection from './ConfigurableSensorSection';
import Copyright from '../internals/components/Copyright';
import { SensorViewProvider, useSensorView } from '../context/SensorViewContext';
import { SensorConfigProvider } from '../context/SensorConfigContext';
import { useGrow } from '../context/GrowContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Stage colors matching GrowProgressBar
const STAGE_COLORS = {
  seedling: '#22c55e',
  vegetative: '#3b82f6',
  flower: '#ec4899',
  lateFlower: '#f97316',
};

// All available sensors with their configurations
const ALL_SENSORS = {
  ph: { id: 'ph', label: 'pH Level', shortLabel: 'pH', unit: '', color: '#8b5cf6' },
  ec: { id: 'ec', label: 'Electrical Conductivity', shortLabel: 'EC', unit: 'mS/cm', color: '#06b6d4' },
  tds: { id: 'tds', label: 'Total Dissolved Solids', shortLabel: 'TDS', unit: 'ppm', color: '#eab308' },
  waterTemp: { id: 'waterTemp', label: 'Water Temperature', shortLabel: 'Water Temp', unit: '°F', color: '#14b8a6' },
  orp: { id: 'orp', label: 'Oxidation Reduction', shortLabel: 'ORP', unit: 'mV', color: '#a855f7' },
  airTemp: { id: 'airTemp', label: 'Air Temperature', shortLabel: 'Air Temp', unit: '°F', color: '#ef4444' },
  humidity: { id: 'humidity', label: 'Relative Humidity', shortLabel: 'Humidity', unit: '%', color: '#3b82f6' },
  co2: { id: 'co2', label: 'Carbon Dioxide', shortLabel: 'CO₂', unit: 'ppm', color: '#22c55e' },
  vpd: { id: 'vpd', label: 'Vapor Pressure Deficit', shortLabel: 'VPD', unit: 'kPa', color: '#f97316' },
  ppfd: { id: 'ppfd', label: 'Photon Flux Density', shortLabel: 'PPFD', unit: 'µmol/m²/s', color: '#fbbf24' },
  dli: { id: 'dli', label: 'Daily Light Integral', shortLabel: 'DLI', unit: 'mol/m²/d', color: '#f59e0b' },
};

// Generate mock historical data for the full grow cycle
function generateGrowCycleData(grow, preset, sensorId) {
  const sensor = ALL_SENSORS[sensorId];
  if (!sensor) return [];

  const startDate = new Date(grow.startDate);
  const endDate = grow.endDate ? new Date(grow.endDate) : new Date();
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

  const data = [];
  // Sample every day for full grow cycle
  for (let day = 0; day <= totalDays; day++) {
    const date = new Date(startDate.getTime() + day * 24 * 60 * 60 * 1000);
    // Generate realistic sensor values with some variation
    let baseValue;
    switch (sensorId) {
      case 'ph': baseValue = 6.0 + Math.sin(day * 0.1) * 0.3; break;
      case 'ec': baseValue = 1.5 + Math.sin(day * 0.08) * 0.3; break;
      case 'airTemp': baseValue = 78 + Math.sin(day * 0.15) * 3; break;
      case 'humidity': baseValue = 60 + Math.sin(day * 0.12) * 8; break;
      case 'co2': baseValue = 1100 + Math.sin(day * 0.1) * 150; break;
      case 'vpd': baseValue = 1.0 + Math.sin(day * 0.13) * 0.2; break;
      default: baseValue = 50 + Math.sin(day * 0.1) * 10;
    }
    const noise = (Math.random() - 0.5) * 0.1 * baseValue;
    data.push({
      date,
      value: Number((baseValue + noise).toFixed(2)),
    });
  }
  return data;
}

// Grow Summary component for completed grows
function GrowSummary({ grow, preset, progress }) {
  const theme = useTheme();
  const isCancelled = grow.status === 'cancelled';

  const startDate = new Date(grow.startDate);
  const endDate = grow.endDate ? new Date(grow.endDate) : new Date();
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

  // Calculate stage boundaries for the chart background (includes optimal ranges per sensor)
  const stageBoundaries = React.useMemo(() => {
    if (!preset) return [];

    const boundaries = [];
    let accumulatedDays = 0;
    const stages = ['seedling', 'vegetative', 'flower', 'lateFlower'];

    stages.forEach(stageId => {
      const stageDuration = preset.stages[stageId]?.duration || 0;
      const startPercent = (accumulatedDays / totalDays) * 100;
      accumulatedDays += stageDuration;
      const endPercent = Math.min((accumulatedDays / totalDays) * 100, 100);

      if (startPercent < 100) {
        boundaries.push({
          id: stageId,
          startPercent,
          endPercent,
          color: STAGE_COLORS[stageId],
          // Include optimal sensor ranges for this stage
          sensorRanges: preset.stages[stageId]?.sensors || {},
        });
      }
    });

    return boundaries;
  }, [preset, totalDays]);

  // Calculate the min/max values for a sensor across all data to scale the optimal lines
  const getSensorDataRange = React.useCallback((data) => {
    if (!data || data.length === 0) return { min: 0, max: 100 };
    const values = data.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    // Add 10% padding
    const padding = (max - min) * 0.1;
    return { min: min - padding, max: max + padding };
  }, []);

  // Get sensors from grow's dashboard config
  const configuredSensors = React.useMemo(() => {
    const sensors = new Set();
    grow.dashboardConfig?.sections?.forEach(section => {
      section.sensors?.forEach(sensor => {
        if (ALL_SENSORS[sensor.type]) {
          sensors.add(sensor.type);
        }
      });
    });
    // Default sensors if none configured
    if (sensors.size === 0) {
      return ['ph', 'airTemp', 'humidity', 'co2'];
    }
    return Array.from(sensors);
  }, [grow.dashboardConfig]);

  // Generate chart data for all configured sensors
  const sensorData = React.useMemo(() => {
    const data = {};
    configuredSensors.forEach(sensorId => {
      data[sensorId] = generateGrowCycleData(grow, preset, sensorId);
    });
    return data;
  }, [grow, preset, configuredSensors]);

  // Get x-axis dates from first sensor
  const xAxisData = sensorData[configuredSensors[0]]?.map(d => d.date) || [];

  return (
    <>
      {/* Summary Header */}
      <Card variant="outlined" sx={{
        p: 3,
        mb: 3,
        bgcolor: isCancelled ? 'warning.main' : 'success.main',
        color: 'white',
      }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <CheckCircleIcon sx={{ fontSize: 48 }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700 }}>
              {grow.name} - {isCancelled ? 'Cancelled' : 'Completed'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {preset?.name || 'Unknown Preset'}
            </Typography>
          </Box>
          <Chip
            icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
            label={isCancelled ? 'Grow Cancelled' : 'Harvest Complete'}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
            }}
          />
        </Stack>
      </Card>

      {/* Grow Stats */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CalendarTodayIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Started
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
          </Stack>
        </Card>
        <Card variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CheckCircleIcon sx={{ color: isCancelled ? 'warning.main' : 'success.main' }} />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {isCancelled ? 'Cancelled' : 'Completed'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
          </Stack>
        </Card>
        <Card variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TimelineIcon sx={{ color: 'info.main' }} />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Total Duration
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                {totalDays} days
              </Typography>
            </Box>
          </Stack>
        </Card>
      </Stack>

      {/* Stage Progress Summary */}
      {progress && (
        <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 2 }}>
            Stage Breakdown
          </Typography>
          <Stack direction="row" sx={{ height: 24, borderRadius: 1, overflow: 'hidden', mb: 2 }}>
            {progress.stages?.map((stage, index) => (
              <Box
                key={stage.id}
                sx={{
                  width: `${(stage.duration / progress.totalDuration) * 100}%`,
                  bgcolor: STAGE_COLORS[stage.id],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: index < progress.stages.length - 1 ? '2px solid' : 'none',
                  borderColor: 'background.paper',
                }}
              >
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.65rem' }}>
                  {stage.duration}d
                </Typography>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {progress.stages?.map(stage => (
              <Stack key={stage.id} direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STAGE_COLORS[stage.id] }} />
                <Typography variant="caption" sx={{ fontFamily: 'Inter, sans-serif' }}>
                  {stage.label}: {stage.duration} days
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Card>
      )}

      {/* Full Grow Cycle Charts */}
      <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 2 }}>
        Full Grow Cycle Data
      </Typography>

      {configuredSensors.map(sensorId => {
        const sensor = ALL_SENSORS[sensorId];
        const data = sensorData[sensorId] || [];
        const dataRange = getSensorDataRange(data);

        // Calculate Y position percentage for a value within the data range
        const getYPercent = (value) => {
          if (dataRange.max === dataRange.min) return 50;
          return 100 - ((value - dataRange.min) / (dataRange.max - dataRange.min)) * 100;
        };

        return (
          <Card key={sensorId} variant="outlined" sx={{ p: 2, mb: 2, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: sensor.color }} />
              <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                {sensor.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                ({sensor.unit || 'no unit'})
              </Typography>
            </Stack>

            {/* Chart with phase shaded regions and optimal range lines */}
            <Box sx={{ position: 'relative' }}>
              {/* Phase background regions with optimal range bands */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 50,
                  right: 20,
                  bottom: 30,
                  display: 'flex',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                {stageBoundaries.map(boundary => {
                  const optimalRange = boundary.sensorRanges[sensorId];
                  const hasOptimalRange = optimalRange && optimalRange.optimalMin !== undefined && optimalRange.optimalMax !== undefined;

                  // Calculate positions for optimal range lines
                  const minYPercent = hasOptimalRange ? getYPercent(optimalRange.optimalMin) : null;
                  const maxYPercent = hasOptimalRange ? getYPercent(optimalRange.optimalMax) : null;

                  return (
                    <Box
                      key={boundary.id}
                      sx={{
                        width: `${boundary.endPercent - boundary.startPercent}%`,
                        height: '100%',
                        bgcolor: `${boundary.color}15`,
                        borderRight: '1px solid',
                        borderColor: `${boundary.color}30`,
                        position: 'relative',
                      }}
                    >
                      {/* Optimal range band (shaded area between min and max) */}
                      {hasOptimalRange && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: `${Math.min(minYPercent, maxYPercent)}%`,
                            height: `${Math.abs(maxYPercent - minYPercent)}%`,
                            bgcolor: `${boundary.color}20`,
                            borderTop: `2px dashed ${boundary.color}`,
                            borderBottom: `2px dashed ${boundary.color}`,
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* The actual chart */}
              <LineChart
                height={200}
                xAxis={[{
                  data: xAxisData,
                  scaleType: 'time',
                  min: xAxisData[0],
                  max: xAxisData[xAxisData.length - 1],
                  tickLabelStyle: {
                    fontSize: 10,
                    fill: theme.palette.text.secondary,
                    fontFamily: 'Inter, sans-serif',
                  },
                  valueFormatter: (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                }]}
                yAxis={[{
                  min: dataRange.min,
                  max: dataRange.max,
                  tickLabelStyle: {
                    fontSize: 10,
                    fill: theme.palette.text.secondary,
                    fontFamily: 'Inter, sans-serif',
                  },
                }]}
                series={[{
                  data: data.map(d => d.value),
                  color: sensor.color,
                  showMark: false,
                  curve: 'monotoneX',
                  area: true,
                  valueFormatter: (v) => `${v} ${sensor.unit}`,
                }]}
                margin={{ top: 10, right: 20, bottom: 30, left: 50 }}
                slotProps={{
                  legend: { hidden: true },
                }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  '& .MuiAreaElement-root': {
                    fillOpacity: 0.15,
                  },
                  '& .MuiLineElement-root': {
                    strokeWidth: 2,
                  },
                }}
              />
            </Box>

            {/* Legend: Stage colors + optimal range indicator */}
            <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'center', alignItems: 'center' }} flexWrap="wrap" useFlexGap>
              {stageBoundaries.map(boundary => (
                <Stack key={boundary.id} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: `${boundary.color}40` }} />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                    {boundary.id.charAt(0).toUpperCase() + boundary.id.slice(1).replace('Flower', ' Flower')}
                  </Typography>
                </Stack>
              ))}
              <Box sx={{ width: 1, height: 12, bgcolor: 'divider', mx: 0.5 }} />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ width: 12, height: 6, border: '1px dashed', borderColor: 'text.secondary', bgcolor: 'action.hover' }} />
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                  Optimal Range
                </Typography>
              </Stack>
            </Stack>
          </Card>
        );
      })}
    </>
  );
}

// Get all unique sensor types from grow's sections
function getSensorsFromSections(sections) {
  const sensorTypes = new Set();
  sections?.forEach(section => {
    section.sensors?.forEach(sensor => {
      sensorTypes.add(sensor.type);
    });
  });
  return Array.from(sensorTypes);
}

// Sensor sections component that reads from grow's config
function SensorSections({ growId, showOverview, showReadings, onToggleOverview, onToggleReadings }) {
  const { grows, updateGrowDashboardConfig } = useGrow();
  const { viewMode, setViewMode } = useSensorView();
  const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);

  // Get sections from grow's dashboard config
  const currentGrow = grows.find(g => g.id === growId);
  const sections = React.useMemo(() => {
    return currentGrow?.dashboardConfig?.sections || [];
  }, [currentGrow?.dashboardConfig?.sections]);

  // Get sensors that are configured in sections for SensorOverview
  const configuredSensors = React.useMemo(() => {
    return getSensorsFromSections(sections);
  }, [sections]);

  // Add section to this grow's config
  const addSection = () => {
    if (!growId) return;
    const newSection = {
      id: 'section-' + Math.random().toString(36).substr(2, 9),
      title: 'New Section',
      sensors: [],
    };
    updateGrowDashboardConfig(growId, {
      sections: [...sections, newSection],
    });
  };

  const handleAddClick = (event) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddClose = () => {
    setAddMenuAnchor(null);
  };

  const handleAddOverview = () => {
    onToggleOverview(true);
    handleAddClose();
  };

  const handleAddReadings = () => {
    onToggleReadings(true);
    handleAddClose();
  };

  const handleAddSensorSection = () => {
    addSection();
    handleAddClose();
  };

  return (
    <>
      {/* Sensor Overview (if enabled) */}
      {showOverview && (
        <Box sx={{ mb: 3 }}>
          <SensorOverview
            onRemove={() => onToggleOverview(false)}
            defaultSensors={configuredSensors}
            growId={growId}
          />
        </Box>
      )}

      {/* Current Readings (if enabled) */}
      {showReadings && (
        <Box sx={{ mb: 3 }}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                Current Readings
              </Typography>
              <IconButton size="small" onClick={() => onToggleReadings(false)}>
                <AddIcon sx={{ transform: 'rotate(45deg)', fontSize: 18 }} />
              </IconButton>
            </Stack>
            <SensorStatusBar configuredSensors={configuredSensors} />
          </Card>
        </Box>
      )}

      {/* Grow System Section Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }}
        >
          Grow System
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                fontFamily: 'Inter, sans-serif',
                textTransform: 'none',
              },
            }}
          >
            <ToggleButton value="meters">
              <SpeedIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Meters
            </ToggleButton>
            <ToggleButton value="charts">
              <BarChartIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Charts
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddClick}
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
            }}
          >
            Add Component
          </Button>
        </Stack>
      </Stack>

      {/* Sensor Module Sections */}
      {sections.length > 0 ? (
        sections.map((section) => (
          <Box key={section.id} sx={{ mb: 3 }}>
            <ConfigurableSensorSection section={section} growId={growId} />
          </Box>
        ))
      ) : (
        <Card
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            mb: 3,
            borderStyle: 'dashed',
          }}
        >
          <SettingsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif', mb: 2 }}
          >
            No sensor modules configured yet
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddSensorSection}
            sx={{
              px: 1.5,
              py: 0.5,
              fontSize: '0.75rem',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'none',
            }}
          >
            Add Sensor Section
          </Button>
        </Card>
      )}

      {/* Add Component Menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
            Charts & Plots
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleAddOverview} disabled={showOverview}>
          <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sensor Overview" secondary="Multi-sensor time series chart" />
        </MenuItem>
        <MenuItem onClick={handleAddReadings} disabled={showReadings}>
          <ListItemIcon><SpeedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Current Readings" secondary="Live sensor values bar" />
        </MenuItem>
        <Divider />
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
            Grow System
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleAddSensorSection}>
          <ListItemIcon><ViewModuleIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="New Sensor Section" secondary="Add sensor cards and gauges" />
        </MenuItem>
      </Menu>
    </>
  );
}

function GrowContent() {
  const { growId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { grows, setActiveGrowId, activeGrow, updateGrowDashboardConfig, getGrowProgress, getPresetForGrow } = useGrow();

  // Determine if we're in demo mode based on the URL
  const isDemo = location.pathname.startsWith('/demo');
  const basePath = isDemo ? '/demo' : '/app';

  // Set the active grow based on the URL parameter
  React.useEffect(() => {
    if (growId) {
      const grow = grows.find(g => g.id === growId);
      if (grow) {
        setActiveGrowId(growId);
      } else {
        // Grow not found, redirect to dashboard
        navigate(basePath);
      }
    }
  }, [growId, grows, setActiveGrowId, navigate, basePath]);

  const currentGrow = grows.find(g => g.id === growId);
  const dashboardConfig = currentGrow?.dashboardConfig || { showOverview: true, showReadings: true, sections: [] };

  // Toggle handlers that persist to grow config
  const handleToggleOverview = (value) => {
    if (growId) {
      updateGrowDashboardConfig(growId, { showOverview: value });
    }
  };

  const handleToggleReadings = (value) => {
    if (growId) {
      updateGrowDashboardConfig(growId, { showReadings: value });
    }
  };

  if (!currentGrow) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Header />
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
            Grow not found
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(basePath)}
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </Card>
      </Box>
    );
  }

  // Check if this is a completed or cancelled grow - show summary view instead
  const isCompletedGrow = currentGrow.status === 'completed' || currentGrow.status === 'cancelled';
  const progress = getGrowProgress(currentGrow.id);
  const preset = getPresetForGrow(currentGrow.id);

  // Render summary view for completed/cancelled grows
  if (isCompletedGrow) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        {/* Header with breadcrumbs, calendar, etc */}
        <Header />

        {/* Grow Summary for completed grows */}
        <Box sx={{ mt: 2 }}>
          <GrowSummary grow={currentGrow} preset={preset} progress={progress} />
        </Box>

        <Copyright sx={{ my: 4 }} />
      </Box>
    );
  }

  // Render active grow dashboard
  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* Header with breadcrumbs, calendar, etc */}
      <Header />

      {/* Grow Selector and Info (includes name, traits, stage selector) */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <GrowSelector />
      </Box>

      {/* Grow Progress Bar */}
      {activeGrow && (
        <Box sx={{ mb: 3 }}>
          <GrowProgressBar />
        </Box>
      )}

      {/* Grow Reminders */}
      {activeGrow && (
        <Box sx={{ mb: 3 }}>
          <GrowReminders growId={growId} />
        </Box>
      )}

      {/* Configurable Components */}
      <SensorSections
        growId={growId}
        showOverview={dashboardConfig.showOverview}
        showReadings={dashboardConfig.showReadings}
        onToggleOverview={handleToggleOverview}
        onToggleReadings={handleToggleReadings}
      />

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}

export default function GrowDashboard(props) {
  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <SensorConfigProvider>
              <SensorViewProvider>
                <GrowContent />
              </SensorViewProvider>
            </SensorConfigProvider>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
